import type { WidgetConfig, WidgetDataSource } from './infrastructure/contracts/widget.contract';

export type WidgetState = 'loading' | 'ready' | 'empty' | 'error' | 'unavailable';

export interface WidgetStateInput {
  source?: WidgetDataSource;
  status: 'idle' | 'loading' | 'ready' | 'error';
  httpStatus?: number;
  value?: unknown;
  /** What a zero MEANS on this tile, e.g. '0 leads today'. Required to call it empty. */
  zeroLabel?: string;
  parentStates?: Record<string, WidgetState>;
}

const UNROUTED = new Set([404, 501]);

/**
 * `unavailable` and `error` are different facts and must not share a look: a
 * missing endpoint is a gap in the product, a 500 is a failure today. Only a
 * widget that can say what its zero means is allowed to render `empty`.
 */
export function resolveWidgetState(input: WidgetStateInput): WidgetState {
  const { source, status, httpStatus, value, zeroLabel, parentStates } = input;
  if (!source || source.kind === 'none') return 'unavailable';
  // A composite carried over from the old dashboards renders its own loading,
  // error and empty states; `undeclared` records an unaudited source, not a gap
  // in what the user sees.
  if (source.kind === 'undeclared') return status === 'error' ? 'error' : status === 'loading' || status === 'idle' ? 'loading' : 'ready';
  if (source.kind === 'derived') {
    const parents = Object.values(parentStates ?? {});
    if (parents.includes('unavailable')) return 'unavailable';
    if (parents.includes('error')) return 'error';
    if (parents.includes('loading') || parents.length === 0) return 'loading';
    return 'ready';
  }
  if (status === 'idle' || status === 'loading') return 'loading';
  if (status === 'error') return httpStatus && UNROUTED.has(httpStatus) ? 'unavailable' : 'error';
  if (value === 0 || value === '0' || (Array.isArray(value) && value.length === 0)) {
    return zeroLabel ? 'empty' : 'ready';
  }
  return 'ready';
}

export function unavailableReason(config: Pick<WidgetConfig, 'dataSource'>): string {
  const s = config.dataSource;
  if (!s) return 'No source declared for this number.';
  if (s.kind === 'none') return s.reason;
  if (s.kind === 'undeclared') return s.note;
  if (s.kind === 'derived') return `Depends on ${s.from.join(', ')}, which is not live yet.`;
  return `No live source yet — ${s.endpoint}`;
}
